import { Stack, Box } from '@mui/material';
import { TooltipProps } from 'recharts/types/component/Tooltip';
import { useTheme } from '@mui/material/styles';

import { Typography } from '@occc/ui-design-system';
import { DateTimeFormat, formatDateTime } from '@occc-app/lib/date';

import { ChartName } from './retailer-network-performance-chart.types';

import { formatPerformanceNumber } from '../../retailer-network-performance.utils';

type CustomTooltipProps = TooltipProps<number, string> & { chartName: ChartName };

const chartNameToLabelMap: Record<ChartName, string> = {
  newMembers: 'New Members',
  loyaltyTransactions: 'New Transactions',
};

export const CustomTooltip = (props: CustomTooltipProps) => {
  const theme = useTheme();

  const entry = props.payload?.[0]?.payload;
  const dailyNew = entry?.dailyNew;
  const progressForDate = entry?.progressForDate;
  const goalForDate = entry?.goalForDate;
  const date = entry?.date;

  const progressToGoalDifference = progressForDate - goalForDate;

  const isTooltipVisible = date && (dailyNew || progressForDate);
  const isOverByShown = progressToGoalDifference !== 0 && progressToGoalDifference > 0;
  const isShortByShown = progressToGoalDifference !== 0 && progressToGoalDifference < 0;

  const totalColor = theme.palette.info.main;
  const dailyColor = theme.palette.info.dark;
  const overByColor = theme.palette.success.main;
  const shortByColor = theme.palette.error.main;

  if (isTooltipVisible) {
    return (
      <Stack p={2} borderRadius={2} bgcolor='background.paper' boxShadow={2}>
        <Typography variant='subtitle2' gutterBottom>
          {formatDateTime(date, DateTimeFormat.DayOfWeekWithDate)}
        </Typography>
        <Box display='flex' justifyContent='space-between' mb={0.3}>
          <Typography variant='body2' color='textSecondary'>
            Daily {chartNameToLabelMap[props.chartName]}
          </Typography>
          <Typography variant='subtitle2' color={dailyColor} pl={1}>
            {formatPerformanceNumber(dailyNew)}
          </Typography>
        </Box>
        <Box display='flex' justifyContent='space-between' mb={0.3}>
          <Typography variant='body2' color='textSecondary'>
            Total {chartNameToLabelMap[props.chartName]}
          </Typography>
          <Typography variant='subtitle2' color={totalColor} pl={1}>
            {formatPerformanceNumber(progressForDate)}
          </Typography>
        </Box>

        {isOverByShown && (
          <Box display='flex' justifyContent='space-between'>
            <Typography variant='body2' color='textSecondary'>
              Over by
            </Typography>
            <Typography variant='subtitle2' color={overByColor} pl={1}>
              {formatPerformanceNumber(progressToGoalDifference)}
            </Typography>
          </Box>
        )}

        {isShortByShown && (
          <Box display='flex' justifyContent='space-between'>
            <Typography variant='body2' color='textSecondary'>
              Short by
            </Typography>
            <Typography variant='subtitle2' color={shortByColor} pl={1}>
              {formatPerformanceNumber(Math.abs(progressToGoalDifference))}
            </Typography>
          </Box>
        )}
      </Stack>
    );
  }

  return null;
};
